import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isValidPercentageDecimal,
  useGetFootprintingDairyPrimaryDataById,
  useMutateFootprintingDairyPrimaryData,
  useTranslation,
} from "utils";

import { Card, Flex, Form, FormInstance, InputNumber, PageSpin, SelectableImageCard, Typography } from "components";

interface DairyGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const ESTIMATED_FORAGE_LOSS = [
  { id: "low", translationKey: "low", icon: "https://klimpublic.s3.eu-central-1.amazonaws.com/green-dot.svg", estimatedForageLossPct: 0.03 },
  { id: "medium", translationKey: "medium", icon: "https://klimpublic.s3.eu-central-1.amazonaws.com/orange-dot.svg", estimatedForageLossPct: 0.15 },
  { id: "high", translationKey: "high", icon: "https://klimpublic.s3.eu-central-1.amazonaws.com/red-dot.svg", estimatedForageLossPct: 0.27 },
] as const;

const FeedForageLossForm = ({ primaryDataId, readOnly, farmId, year }: DairyGeneralProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);

  const [forageLossPct, setforageLossPct] = useState<number | null>(null);

  const { data: primaryDataQuery, isSuccess, isLoading } = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");

  const { mutate: mutateForageLoss } = useMutateFootprintingDairyPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      forageLossPct,
    },
    primaryDataId ?? ""
  );

  useEffect(() => {
    if (primaryDataQuery && isSuccess) {
      setforageLossPct(primaryDataQuery.forageLossPct ?? null);

      formRef.current?.setFieldsValue({
        forageLossPct: formatFractionInput(primaryDataQuery.forageLossPct ?? null),
      });

      const fieldsToValidate = (["forageLossPct"] as const).some((field) => primaryDataQuery[field] !== null && primaryDataQuery[field] !== undefined);

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [primaryDataQuery, isSuccess]);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateForageLoss({});
      }
    },
    [isLoading, mutateForageLoss, readOnly]
  );

  const handleInputChange = (v: number | null) => {
    setforageLossPct(formatPercentageInput(v));
  };

  const handleCardSelection = (id: string) => {
    const selectedCard = ESTIMATED_FORAGE_LOSS.find((fl) => fl.id === id);
    const estimatedForageLossPct = selectedCard?.estimatedForageLossPct ?? null;

    setforageLossPct(estimatedForageLossPct);
    formRef.current?.setFieldsValue({
      forageLossPct: formatFractionInput(estimatedForageLossPct),
    });
  };

  const checkedForageLossCard = (cardId: string): boolean => {
    if (forageLossPct === null) return false;
    const matchingCard = ESTIMATED_FORAGE_LOSS.find((fl) => fl.estimatedForageLossPct === forageLossPct);
    return matchingCard?.id === cardId;
  };

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Form ref={formRef}>
      <Card border dataTestId="footprinting-dairy-forage-loss" className="padding-200">
        <Flex vertical gap={16}>
          <Flex vertical gap={8}>
            <Typography variant="h4">{t("dairyFeedForageLossForm.forageLossQuestion")}</Typography>
            <Typography variant="paragraph-m" color="grey-600">
              {t("dairyFeedForageLossForm.forageLossQuestionDescription")}
            </Typography>
          </Flex>

          {ESTIMATED_FORAGE_LOSS.map((fl) => (
            <SelectableImageCard
              key={fl.id}
              image={{
                imageSrc: fl.icon,
                imageAlt: "forage-loss-icon",
                imageSize: 48,
              }}
              selectionMode="single"
              title={t(`dairyFeedForageLossForm.estimatedForageLoss.${fl.translationKey}.title`)}
              titleVariant="paragraph-m"
              description={t(`dairyFeedForageLossForm.estimatedForageLoss.${fl.translationKey}.description`)}
              onChange={() => handleCardSelection(fl.id)}
              checked={checkedForageLossCard(fl.id)}
              disabled={readOnly}
            />
          ))}

          <Form.Item>
            <Form.Item>
              <InputNumber
                disabled={readOnly}
                name="forageLossPct"
                dataTestId="footprinting-dairy-feed-forage-pct-input"
                label={t("dairyFeedForageLossForm.question.forageLossPctInput")}
                suffix={t("dairyFeedForageLossForm.question.forageLossPctSuffix")}
                value={formatFractionInput(forageLossPct)}
                initialValue={formatFractionInput(forageLossPct)}
                onChange={handleInputChange}
                precision={2}
                rules={[
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) => {
                      if (!isValidPercentageDecimal(v)) {
                        return Promise.reject(new Error(t("dairyFeedForageLossForm.error.validPercentage")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Form.Item>
          </Form.Item>
        </Flex>
      </Card>
    </Form>
  );
};

FeedForageLossForm.Key = "dairy.feedForageLoss";
export { FeedForageLossForm };
